/**
 * parsed-body-doc-from-viewer: 뷰어가 메일 본문 표시용 파싱 직후(mutate 전) 떠서 전달한
 * 원본 보존 Document(clone)의 host 측 보관소. draft 편집·reply/replyAll/forward·다시 보내기가
 * 본문 전체 재파싱(2.7MB WebKit ~4s) 대신 이 Document 를 소비한다 (draft/resend 는
 * reopenParsedDoc 레일, reply/forward 는 bakeQuotedBodyLazy 입력 — draft-reopen-parse-share 계약).
 *
 * 캡처 대상 선별은 2단 게이트(역할이 달라 중복이 아님 — 한쪽 제거 금지):
 *   - Gate B(정책, host 데이터): EmailInfoData.enable_capture_element — JMAP 메일(+draft 인
 *     ScheduleEmail)만 true. EML·플래그 미전송 host(om-platform 미이식 등)는 기본 false 로
 *     캡처 자체가 꺼진다(fail-safe).
 *   - Gate A(전송 능력): 뷰어 ViewerIFace 가 same-realm 직접 핸들러 존재를 확인한 뒤에만
 *     clone 을 생성한다 — viewerExt 의 핸들러 등록(BODY_DOC_FROM_VIEWER)이 곧 기능 토글.
 *   크기 실익 판단(≥256KB)은 body 문자열을 아는 뷰어 캡처 지점의 책임이다.
 *
 * stale 방어: 엔트리에 원문 문자열(sourceHTML)을 함께 보관하고, 소비 시점의 실제 본문과
 * **완전 일치(===)** 할 때만 반환한다(불일치 → 엔트리 폐기, 기존 전체 파싱 폴백). 소비처의
 * 본문(bodyPart.getHTMLBody)은 매 호출 재조립되는 비캐시 문자열이라 참조가 아닌 값 비교이며,
 * 이 === 가드가 뷰어/편집기 본문 가공 경로의 모든 비대칭에 대한 최종 안전망이다. draft 저장은
 * JMAP 특성상 새 email id 를 만들므로 id 충돌형 stale 은 구조적으로 드물지만 이중 방어한다.
 *
 * 메모리: 엔트리당 파싱 DOM(수~수십 MB) + 원문 문자열. thread 전환 시 전체 clear
 * (viewerExt applyThread 의 threadChanged) + 뷰어 언마운트 clear(disposeViewerApi) +
 * 엔트리 수 상한(FIFO)으로 bound. 소비는 take-once — 반환된 Document 는 cid 변환이
 * mutate 하고 setDataFromDOM/getSafeElement 가 소진하므로 재보관하지 않는다.
 */

/** 기능 토글 — viewerExt(수신 핸들러 등록 = Gate A)와 EmailForm(소비)이 공유하는 단일 스위치. */
export const BODY_DOC_FROM_VIEWER = true

interface ParsedBodyDocEntry {
  doc: Document
  sourceHTML: string
}

/**
 * 전 메일(≥256KB) 캡처 확장에 맞춘 상한 — 대형 메일 다수 thread 에서 reply 대상 엔트리가
 * 조기에 밀리지 않도록 5(독립 검증 권고·사용자 승인). 밀린 메일은 자체 파싱 폴백이라
 * 정확성 무영향(성능 기회 손실뿐).
 */
const MAX_ENTRIES = 5

const cache = new Map<string, ParsedBodyDocEntry>()

export function setParsedBodyDocument(emailId: string, doc: Document, sourceHTML: string): void {
  if (emailId.length === 0 || sourceHTML.length === 0) return
  // cross-realm(IFrameViewer) Document 는 host realm 소유 복제로 격리한다 — iframe thread 전환/
  // 폐기 후에도 생존(수명), host baseURI(InnerViewer 와 동일 → parity), host `instanceof` 정체성
  // 을 한 번의 importNode 로 동시 해결. same-realm(InnerViewer) 은 뷰어가 이미 clone 을 넘기므로
  // 추가 복제 없이 그대로 보관한다.
  const hostOwned = doc instanceof Document ? doc : importIntoHostRealm(doc)
  // 재보고(재펼침·리사이즈 재bake) 시 최신값으로 교체 + FIFO 순서 갱신.
  cache.delete(emailId)
  cache.set(emailId, { doc: hostOwned, sourceHTML })
  if (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value
    if (oldest != null) cache.delete(oldest)
  }
}

/**
 * cross-realm Document 를 host realm 소유 HTMLDocument 로 복제한다. `importNode`(복사)이며
 * `adoptNode`(이동)는 뷰어의 live doc 노드를 뺏어 표시를 깨므로 쓰지 않는다. importNode 는
 * 토크나이저를 거치지 않는 네이티브 트리 복제라 재파싱(~4s/2.7MB WebKit)이 아니라 수십~수백 ms.
 */
function importIntoHostRealm(src: Document): Document {
  const hostDoc = document.implementation.createHTMLDocument('')
  const importedRoot = hostDoc.importNode(src.documentElement, true)
  hostDoc.replaceChild(importedRoot, hostDoc.documentElement)
  return hostDoc
}

/**
 * Document 소비(take-once) + stale 검증. 현재 본문(bodyHTML)과 원문이 완전 일치할 때만
 * Document 를 반환하며, 일치 여부와 무관하게 엔트리는 제거된다(반환된 DOM 은 소비처가
 * mutate/소진, 불일치는 본문 변경이므로 폐기가 맞다).
 */
export function takeParsedBodyDocument(emailId: string, bodyHTML: string): Document | null {
  const entry = cache.get(emailId)
  if (entry == null) return null
  cache.delete(emailId)
  return entry.sourceHTML === bodyHTML ? entry.doc : null
}

/** thread 전환(applyThread threadChanged)·뷰어 언마운트 시 전체 폐기 — 상주를 현재 thread 로 한정. */
export function clearParsedBodyDocuments(): void {
  cache.clear()
}
